/**
 * Duplicate a field record (P2-6's duplicate half, B-198; P2-5 spec Phase 3).
 *
 * A duplicate is a NEW CREATE DRAFT seeded from the source, not a clone of a
 * synced row. The record form opens it in create mode with TOOL ID focused and
 * selected, and the rep's Create tap runs the create path unchanged: the shared
 * duplicate check, the pending row, the queued tool_create, the grid append.
 * Nothing persists until Create, so a mistaken Duplicate costs one Close.
 *
 * WHAT THE COPY CARRIES (Jeff, 2026-10-22: everything except photos), with the
 * judgements the ruling left to the build:
 *   - Every field the field zone can write (EDITABLE_FIELDS) that holds a
 *     value, each marked dirty so the post-create follow-up capture sends it.
 *     Keys the zone cannot write are NOT copied: the server would return them
 *     in dropped_fields and the copy would never settle (B-203).
 *   - NOT found / added_by_tmg: the source's values describe how THAT record
 *     came to exist, not the copy. The copy is seeded checked instead, as
 *     mkDraft seeds a fresh create (D-5, Jeff 2026-10-28). NOT_COPIED keeps the
 *     loop below from overwriting that seed with the source's values.
 *   - NOT the server-owned fields (estimated_tool_size, calculated_weight,
 *     material_source; EDITABLE_FIELDS already excludes them): the follow-up
 *     capture recomputes them from the copied dimensions and weight.
 *   - material only when the source's is not FORMULA-derived. A client-sent
 *     material latches material_source=MANUAL for good (_apply_capture_formulas),
 *     so copying a derived value would stamp the copy as hand-entered; left
 *     blank, the server derives it again from the copied tool type.
 *   - Part numbers as NEW entries (id null, a fresh local_key): their ids name
 *     the source's child documents. The server mints new ones.
 *   - NOT asset tags (Jeff, 2026-10-22): a tag is a sticker on one physical
 *     tool. Part numbers describe what the tool makes and are genuinely shared
 *     across a family; four identical fixtures do not share a tag.
 *   - No media (the ruling). No baseline, no etag, no record id: pending state
 *     is created by Create, exactly as for a typed-in tool.
 *   - TOOL ID: the source's, incremented (nextFamilyToolId).
 */
import { EDITABLE_FIELDS } from './editableFields';
import { toolIdMatchKey } from './toolIdMatch';

// A trailing number counts as a family suffix only after a non-digit: '285N-1',
// 'AB12'. An all-digit TOOL ID ('6640') is one number, not a family member, and
// gets '-2' like an ID with no number at all.
const TRAILING_NUMBER = /^(.*\D)(\d+)$/;

/** Adds one to a digit string, keeping its zero padding: '009' -> '010', '99' -> '100'. */
function addOne(digits) {
  const a = digits.split('');
  for (let i = a.length - 1; i >= 0; i -= 1) {
    if (a[i] !== '9') {
      a[i] = String(Number(a[i]) + 1);
      return a.join('');
    }
    a[i] = '0';
  }
  return `1${a.join('')}`;
}

/**
 * The next TOOL ID in a family: '285N-1' -> '285N-2'; no trailing number ->
 * '<id>-2'. String arithmetic, so a long run of digits never loses precision.
 * A blank ID stays blank (the rep must type one; Create requires it).
 */
export function incrementToolId(toolId) {
  const s = typeof toolId === 'string' ? toolId.trim() : '';
  if (!s) return '';
  const m = TRAILING_NUMBER.exec(s);
  return m ? `${m[1]}${addOne(m[2])}` : `${s}-2`;
}

/**
 * The duplicate's TOOL ID: the source's incremented, then incremented again
 * past any ID a stored row at the site already holds (the shared rule:
 * trimmed, case-insensitive). Duplicating 285N-1 twice therefore gives 285N-2
 * then 285N-3, not 285N-2 twice — the duplicate warning fires on neither.
 * B-198's ruling: incrementing is what stops the duplicate button firing the
 * duplicate warning on every tap.
 *
 * @param {string} sourceToolId
 * @param {Object[]} rows — the site's stored rows
 */
export function nextFamilyToolId(sourceToolId, rows) {
  const taken = new Set((rows || []).map((r) => toolIdMatchKey(r?.working?.supplier_tool_id)).filter(Boolean));
  let candidate = incrementToolId(sourceToolId);
  for (let n = 0; candidate && n < 1000 && taken.has(toolIdMatchKey(candidate)); n += 1) {
    candidate = incrementToolId(candidate);
  }
  return candidate;
}

const NOT_COPIED = new Set(['found', 'added_by_tmg']);
const isBlank = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');

/**
 * The create draft for a duplicate of `source` — the same shape mkDraft
 * (FieldRecordModal) returns, so the create path takes it unchanged.
 *
 * @param {Object} source — the record being duplicated (a stored row)
 * @param {Object[]} rows — the site's stored rows, for the TOOL ID increment
 * @param {{siteId?: string, newKey?: () => string}} [opts]
 */
export function buildDuplicateDraft(source, rows, { siteId, newKey = () => crypto.randomUUID() } = {}) {
  const from = (source && source.working) || {};
  const working = { weight_unit: 'LBS', found: true, added_by_tmg: true };
  const dirty = { weight_unit: true, found: true, added_by_tmg: true };
  for (const f of EDITABLE_FIELDS) {
    if (NOT_COPIED.has(f.key)) continue;
    if (f.key === 'material' && from.material_source === 'FORMULA') continue;
    const v = from[f.key];
    if (isBlank(v)) continue;
    working[f.key] = v;
    dirty[f.key] = true;
  }
  working.supplier_tool_id = nextFamilyToolId(from.supplier_tool_id, rows);
  dirty.supplier_tool_id = true;

  const merged = (group) => [...((source && source[group]) || []), ...((source && source[`extra_${group}`]) || [])]
    .filter((e) => e && !isBlank(e.value));
  const partNumbers = merged('part_numbers').map((e) => ({
    id: null,
    local_key: newKey(),
    value: e.value,
    type: e.type || 'TBD',
    ...(isBlank(e.part_description) ? {} : { part_description: e.part_description }),
  }));
  if (partNumbers.length) dirty.part_numbers = true;

  return {
    record_id: null,
    tool_id: null,
    site_id: siteId ?? source?.site_id ?? null,
    etag: null,
    baseline: {},
    working,
    dirty,
    part_numbers: partNumbers,
    extra_part_numbers: [],
    asset_tags: [], // never copied: a tag belongs to one physical tool
    extra_asset_tags: [],
    media: [],
    cached_at: null,
  };
}
